import { formatDate } from '@seen/core/dates'
import { CcdaCommunicationRequest } from '@seen/core/models/communicationRequests/CcdaCommunicationRequest'
import type { Participant } from '@seen/core/models/patients/participant'
import {
  toast,
  useCreateMutation,
  useCurrentPractitioner,
  useMedplum,
} from '@seen/react'
import { useMutation } from '@tanstack/react-query'

interface UseCcdaExportResult {
  exportCcda: (participant: Participant) => Promise<CcdaCommunicationRequest>
  isExporting: boolean
  error: Error | null
}

export function useCcdaExport(): UseCcdaExportResult {
  const medplum = useMedplum()
  const currentPractitioner = useCurrentPractitioner()

  const { mutateAsync: createCcdaCommunicationRequest } =
    useCreateMutation<CcdaCommunicationRequest>()
  const mutation = useMutation<CcdaCommunicationRequest, Error, Participant>({
    mutationFn: async (participant: Participant) => {
      const url = medplum.fhirUrl('Patient', participant.id, '$ccda-export')
      const ccdaXml = await medplum.post(url, {})

      const xmlString =
        typeof ccdaXml === 'string'
          ? ccdaXml
          : new XMLSerializer().serializeToString(ccdaXml as Document)

      const binary = await medplum.createBinary({
        data: xmlString,
        contentType: 'application/cda+xml',
        filename: `ccda-${participant.seenMRN ?? participant.id}-${formatDate(new Date())}.xml`,
      })

      const communicationRequest = new CcdaCommunicationRequest({
        subject: participant.reference,
        ccdaBinary: binary,
        ccdaTitle: `CCDA Export - ${participant.name}`,
        requester: currentPractitioner.reference,
      })

      await createCcdaCommunicationRequest(communicationRequest)

      return communicationRequest
    },
    onSuccess: () => {
      toast.success('CCDA export created')
    },
  })

  return {
    exportCcda: (participant: Participant) => mutation.mutateAsync(participant),
    isExporting: mutation.isPending,
    error: mutation.error,
  }
}
